import { useCallback, useEffect, useState } from "react";
import {
  adminDeactivateUser,
  adminDeleteUser,
  adminListUsers,
  adminReactivateUser,
  adminUpdateUser,
  friendlyMessage,
} from "../../controllers/accountApi";
import type { GetToken } from "../../controllers/accountApi";
import type { Account } from "../../models/account";

const TYPE_LABEL: Record<string, string> = { INDIVIDUAL: "개인", COMPANY: "기업" };
const STATUS_LABEL: Record<string, string> = { ACTIVE: "활성", SUSPENDED: "비활성", DELETED: "삭제됨" };

// Admin · 유저 관리: view every account's entered info, edit contact fields, and deactivate /
// reactivate / (soft) delete accounts. Backed by /api/admin/accounts (group:sp-admin / admin).
export function UserManagement({ getToken }: { getToken: GetToken }) {
  const [users, setUsers] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await adminListUsers(getToken));
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { void reload(); }, [reload]);

  async function act(id: string, fn: () => Promise<Account>) {
    setBusyId(id);
    setError(null);
    try {
      const updated = await fn();
      setUsers((us) => us.map((u) => (u.id === id ? updated : u)));
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setBusyId(null);
    }
  }

  if (loading && users.length === 0) return <div className="center">불러오는 중…</div>;

  return (
    <div className="detail-body">
      <div className="detail-main">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <p className="meta" style={{ margin: 0 }}>전체 사용자 {users.length}명 · 입력 정보 확인 / 수정 / 비활성화 / 삭제</p>
          <button className="btn ghost" style={{ width: "auto" }} onClick={() => void reload()}>새로고침</button>
        </div>
        {error && <div className="note" style={{ background: "#fdeef0", marginBottom: 12 }}><b style={{ color: "var(--amber)" }}>오류:</b> {error}</div>}

        <table className="sdui-table">
          <thead>
            <tr><th>이메일</th><th>이름</th><th>유형</th><th>상태</th><th>가입일</th><th>작업</th></tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const busy = busyId === u.id;
              return (
                <>
                  <tr key={u.id} style={{ cursor: "pointer" }} onClick={() => setOpenId(openId === u.id ? null : u.id)}>
                    <td>{u.email}</td>
                    <td>{u.displayName || "-"}</td>
                    <td>{TYPE_LABEL[u.accountType] ?? u.accountType}</td>
                    <td><span className={`badge ${u.status === "ACTIVE" ? "ok" : "locked"}`}>{STATUS_LABEL[u.status] ?? u.status}</span></td>
                    <td>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "-"}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <button className="link-btn" disabled={busy} onClick={() => { setOpenId(u.id); setEditId(editId === u.id ? null : u.id); }}>수정</button>
                        {u.status === "ACTIVE" ? (
                          <button className="link-btn" disabled={busy} onClick={() => void act(u.id!, () => adminDeactivateUser(getToken, u.id!))}>비활성화</button>
                        ) : (
                          <button className="link-btn" disabled={busy} onClick={() => void act(u.id!, () => adminReactivateUser(getToken, u.id!))}>활성화</button>
                        )}
                        <button className="link-btn" style={{ color: "var(--amber)" }} disabled={busy || u.status === "DELETED"} onClick={() => {
                          if (window.confirm(`${u.email} 계정을 삭제하시겠습니까?`)) void act(u.id!, () => adminDeleteUser(getToken, u.id!));
                        }}>삭제</button>
                      </div>
                    </td>
                  </tr>
                  {openId === u.id && (
                    <tr key={u.id + "-d"}>
                      <td colSpan={6} style={{ background: "var(--bg)" }}>
                        {editId === u.id ? <EditRow user={u} getToken={getToken} onSaved={(nu) => { setUsers((us) => us.map((x) => (x.id === nu.id ? nu : x))); setEditId(null); }} /> : <UserDetail user={u} />}
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
        {users.length === 0 && <div className="sdui-table-empty">사용자가 없습니다.</div>}
      </div>
    </div>
  );
}

function UserDetail({ user }: { user: Account }) {
  const p = user.individualProfile;
  const c = user.companyProfile;
  return (
    <div className="info-card" style={{ margin: "8px 0" }}>
      <h4>입력 정보</h4>
      <div className="kv"><span>전화번호</span><span>{user.phone || "-"}</span></div>
      <div className="kv"><span>언어 / 시간대</span><span>{(user.locale || "-") + " / " + (user.zoneId || "-")}</span></div>
      <div className="kv"><span>약관 동의</span><span>{user.termsAgreedAt ? new Date(user.termsAgreedAt).toLocaleString() : "미동의"}</span></div>
      <div className="kv"><span>마케팅 수신</span><span>{user.marketingOptIn ? "동의" : "미동의"}</span></div>
      {user.accountType === "INDIVIDUAL" && p && (
        <>
          <div className="kv"><span>성명</span><span>{[p.familyName, p.givenName].filter(Boolean).join(" ") || "-"}</span></div>
          <div className="kv"><span>직함 / 소속</span><span>{(p.jobTitle || "-") + " / " + (p.organization || "-")}</span></div>
        </>
      )}
      {user.accountType === "COMPANY" && c && (
        <>
          <div className="kv"><span>법인명 / 브랜드</span><span>{(c.legalName || "-") + " / " + (c.brandName || "-")}</span></div>
          <div className="kv"><span>업종 / 담당자</span><span>{(c.industry || "-") + " / " + (c.contactEmail || "-")}</span></div>
        </>
      )}
    </div>
  );
}

function EditRow({ user, getToken, onSaved }: { user: Account; getToken: GetToken; onSaved: (u: Account) => void }) {
  const [displayName, setDisplayName] = useState(user.displayName ?? "");
  const [phone, setPhone] = useState(user.phone ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function save() {
    setBusy(true); setErr(null);
    try { onSaved(await adminUpdateUser(getToken, user.id!, { displayName, phone })); }
    catch (e) { setErr(friendlyMessage(e)); } finally { setBusy(false); }
  }
  return (
    <div className="info-card" style={{ margin: "8px 0" }}>
      <h4>수정 — {user.email}</h4>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label className="sdui-field"><span className="sdui-label">표시 이름</span><input className="sdui-input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} /></label>
        <label className="sdui-field"><span className="sdui-label">전화번호</span><input className="sdui-input" value={phone} onChange={(e) => setPhone(e.target.value)} /></label>
        <button className="btn primary" style={{ width: "auto" }} disabled={busy} onClick={() => void save()}>{busy ? "저장 중…" : "저장"}</button>
      </div>
      {err && <div className="note" style={{ background: "#fdeef0", marginTop: 8 }}>{err}</div>}
    </div>
  );
}
